import { createAppError } from "./createAppError";
import { SERVER_ERROR_CODE_SET } from "./serverErrorContract";
import type { AppError } from "./types";

interface NormalizedServerPayload {
  code?: string;
  status?: number;
  traceId?: string;
  shouldLogout?: boolean;
  message: string;
  meta?: Record<string, unknown>;
  fieldErrors?: Record<string, string[]>;
}



/**
 * Checks if a value is a record (an object with string keys).
 * @param value unknown - The value to check
 * @returns boolean
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}



/**
 * Converts a value to an array of strings.
 * This function is used to normalize field error messages that can be either a single string or an array of strings in the server response.
 * @param value unknown - The value to convert
 * @returns string[] | undefined
 */
function toStringArray(value: unknown): string[] | undefined {
  if (typeof value === "string") {
    return value.trim().length > 0 ? [value] : undefined;
  }

  if (!Array.isArray(value)) {
    return undefined;
  }

  const messages = value.filter((entry): entry is string => typeof entry === "string");
  return messages.length > 0 ? messages : undefined;
}



/**
 * Extracts the error from the server response data.
 * @param data unknown - The server response data
 * @returns Record<string, unknown> | undefined
 */
function getErrorEnvelope(data: unknown): Record<string, unknown> | undefined {
  if (!isRecord(data)) {
    return undefined;
  }

  const rawError = data.error;
  if (isRecord(rawError)) {
    return rawError;
  }

  return data;
}



/**
 * Extracts field errors from the server response data and error envelope.
 * @param data unknown - The server response data
 * @param envelope Record<string, unknown> | undefined - The error envelope
 * @returns Record<string, string[]> | undefined
 */
function extractFieldErrors(data: unknown, envelope: Record<string, unknown> | undefined): Record<string, string[]> | undefined {
  
  if (!isRecord(data) && !envelope) {
    return undefined;
  }

  const source = (envelope?.fieldErrors ?? (isRecord(data) ? (data["validation errors"] ?? data.errors) : undefined)) as unknown;
  
  if (!isRecord(source)) {
    return undefined;
  }

  const entries = Object.entries(source)
    .map(([key, value]) => [key, toStringArray(value)] as const)
    .filter((entry): entry is readonly [string, string[]] => Array.isArray(entry[1]));

  if (entries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(entries);
}



/**
 * Extracts the error message from the server response data.
 * @param data unknown - The server response data
 * @param envelope Record<string, unknown> | undefined - The error envelope
 * @param fallback string - The fallback message
 * @returns string
 */
function extractMessage(data: unknown, envelope: Record<string, unknown> | undefined, fallback: string): string {
  if (envelope && typeof envelope.message === "string" && envelope.message.trim().length > 0) {
    return envelope.message;
  }

  if (!isRecord(data)) {
    return fallback;
  }

  const directMessage = data.message;
  if (typeof directMessage === "string" && directMessage.trim().length > 0) {
    return directMessage;
  }

  return fallback;
}



/**
 * Resolves the kind of error based on its HTTP status code.
 * @param status number | undefined - The HTTP status code
 * @returns AppError["kind"]
 */
function resolveKindFromStatus(status: number | undefined): AppError["kind"] {
  if (status === 422) return "validation";
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 419) return "csrf";
  if (status === 404) return "notFound";
  if (status === 409) return "conflict";
  if (status === 400) return "business";
  if (typeof status === "number" && status >= 500) return "server";
  return "unexpected";
}



/**
 * This function resolves the error kind based on the provided error code and HTTP status.
 * It first attempts to determine the kind using the error code. If the code is not provided or recognized,
 * it falls back to resolving the kind based on the HTTP status code.
 * @param code string | undefined - The error code
 * @param status number | undefined - The HTTP status code
 * @returns AppError["kind"]
 */
function resolveKindFromCode(code: string | undefined, status: number | undefined): AppError["kind"] {
  
  if (!code) {
    return resolveKindFromStatus(status);
  }

  switch (code) {
    case "AUTH_UNAUTHENTICATED":
      return "auth";
    case "AUTH_INVALID_CREDENTIALS":
    case "VALIDATION_FAILED":
      return "validation";
    case "AUTH_FORBIDDEN":
      return "forbidden";
    case "RESOURCE_NOT_FOUND":
      return "notFound";
    case "DATABASE_CONSTRAINT_VIOLATION":
      return "conflict";
    case "DATABASE_QUERY_ERROR":
    case "INTERNAL_SERVER_ERROR":
      return "server";
    case "BUSINESS_RULE_VIOLATION":
      return "business";
    case "HTTP_ERROR":
      return resolveKindFromStatus(status);
    default:
      return resolveKindFromStatus(status);
  }
}



/**
 * Resolves the default configuration for a given error kind.
 * @param kind AppError["kind"] - The error kind
 * @returns The default configuration for specific error kind
 */
function resolveDefaultsByKind(kind: AppError["kind"]): Pick<AppError, "defaultDisplayMode" | "retryable" | "logLevel"> {
  switch (kind) {
    case "validation":
      return { defaultDisplayMode: "inline" as const, retryable: false, logLevel: "warn" as const };
    case "auth":
      return { defaultDisplayMode: "page" as const, retryable: false, logLevel: "warn" as const };
    case "forbidden":
      return { defaultDisplayMode: "page" as const, retryable: false, logLevel: "warn" as const };
    case "csrf":
      return { defaultDisplayMode: "toast" as const, retryable: true, logLevel: "warn" as const };
    case "notFound":
      return { defaultDisplayMode: "page" as const, retryable: false, logLevel: "warn" as const };
    case "conflict":
      return { defaultDisplayMode: "inline" as const, retryable: false, logLevel: "warn" as const };
    case "business":
      return { defaultDisplayMode: "toast" as const, retryable: false, logLevel: "warn" as const };
    case "server":
      return { defaultDisplayMode: "page" as const, retryable: true, logLevel: "error" as const };
    default:
      return { defaultDisplayMode: "boundary" as const, retryable: false, logLevel: "error" as const };
  }
}


/**
 * Resolves the default configuration for a given error code.
 * @param code string - The error code
 * @returns The default configuration for the specific error code
 */
function resolveDefaultsByCode(code: string): Partial<AppError> {
  switch (code) {
    case "AUTH_INVALID_CREDENTIALS":
      return { defaultDisplayMode: "toast" as const };

    default:
      return {};
  }
}

/**
 * Warns about unknown server error codes in the console.
 * @param code string | undefined - The error code to check
 * @returns void
 */
function warnForUnknownServerCode(code: string | undefined): void {
  if (!code || SERVER_ERROR_CODE_SET.has(code) || !import.meta.env.DEV) {
    return;
  }

  console.warn("[errors] unknown server error code encountered", code);
}



/**
 * Normalizes a server error into a standardized AppError.
 * This function takes the HTTP status code, the server response data, a fallback message, and the original error cause, and produces a normalized AppError object that can be consistently handled across the application.
 * @param status number | undefined - HTTP status code from the server response
 * @param data unknown - The error data from the server response
 * @param fallbackMessage string - A fallback message to use if the server error doesn't provide one
 * @param cause unknown - The original error cause
 * @returns AppError
 */
export function normalizeServerError(status: number | undefined, data: unknown, fallbackMessage: string, cause: unknown): AppError {

  const envelope = getErrorEnvelope(data);
  const resolvedStatus = typeof envelope?.status === "number" ? envelope.status : status;
  const code = typeof envelope?.code === "string" ? envelope.code : undefined;

  warnForUnknownServerCode(code);

  const kind = resolveKindFromCode(code, resolvedStatus);
  const defaults = {
    ...resolveDefaultsByKind(kind),
    ...(code ? resolveDefaultsByCode(code) : null)
  };

  const normalizedPayload: NormalizedServerPayload = {
    code,
    status: resolvedStatus,
    traceId: typeof envelope?.traceId === "string" ? envelope.traceId : undefined,
    shouldLogout: typeof envelope?.shouldLogout === "boolean" ? envelope.shouldLogout : undefined,
    message: extractMessage(data, envelope, fallbackMessage),
    meta: isRecord(envelope?.meta) ? envelope.meta : undefined,
    fieldErrors: extractFieldErrors(data, envelope),
  };

  return createAppError(
    kind,
    normalizedPayload.message,
    cause,
    {
      status: normalizedPayload.status,
      code: normalizedPayload.code,
      traceId: normalizedPayload.traceId,
      shouldLogout: normalizedPayload.shouldLogout ?? kind === "auth",
      meta: normalizedPayload.meta,
      fieldErrors: normalizedPayload.fieldErrors,
      defaultDisplayMode: defaults.defaultDisplayMode,
      retryable: defaults.retryable,
      logLevel: defaults.logLevel,
    }
  );
}
